import React from 'react';
import { SafetyCertificateOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import {
  GovernedAnalysisBlock,
  GovernedAnalysisConfidence,
  ResponseTone,
  SupportConfidenceLevel
} from '../../types';
import HighlightedText from '../HighlightedText';
import { ResponseActions, ResponseIcon } from '../ResponseActionButton';
import { useResponseAction } from '../responseActions';
import { ResponsePanel, ResponseReply } from '../responsePrimitives';
import { responseTone, responseType, rsColor, rsv } from '../responseScale';
import GovernedDonut, { segmentInk } from './answer/GovernedDonut';

// 6424:11849: the panel draws a blue rule under a 1px glow.
const Panel = styled(ResponsePanel)`
  border-color: ${rsColor.link};
  box-shadow: ${rsColor.governedGlow};
`;

const Head = styled.header`
  padding: ${rsv.space('panelHeadTight')};
  border-bottom: 1px solid ${rsColor.border};
`;

const Eyebrow = styled.h4`
  margin: 0;
  color: ${rsColor.link};
  ${responseType('eyebrow')}

  .anticon {
    font-size: ${rsv.size('eyebrowIcon')}px;
    vertical-align: middle;
  }
`;

const Body = styled.div`
  padding: ${rsv.space('governedBody')};

  > * + * {
    margin-top: ${rsv.space('governedGap')}px;
  }
`;

const Prose = styled.p`
  margin: 0;
  color: ${rsColor.secondary};
  white-space: pre-line;
  ${responseType('prose')}
`;

const ConfidenceLine = styled.div`
  display: flex;
  align-items: center;
`;

// 6424:11896.
const Pill = styled.span<{ $ink: string; $fill: string }>`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  padding: ${rsv.space('confidencePill')};
  border: 1px solid ${({ $ink }) => $ink};
  border-radius: ${rsv.radius('button')}px;
  background: ${({ $fill }) => $fill};
  color: ${({ $ink }) => $ink};
  white-space: nowrap;
  ${responseType('confidence')}

  > * + * {
    margin-left: ${rsv.space('confidenceIconGap')}px;
  }

  .anticon {
    font-size: ${rsv.size('confidenceIcon')}px;
  }
`;

// 6424:11900: a bold lead in the text colour, the rest in the body's.
const Reason = styled.p`
  flex: 1 1 auto;
  min-width: 0;
  margin: 0 0 0 ${rsv.space('reasonGap')}px;
  color: ${rsColor.secondary};
  ${responseType('reason')}

  > strong {
    color: ${rsColor.text};
    font-weight: 700;
  }
`;

const CONFIDENCE_TONE: Record<SupportConfidenceLevel, ResponseTone> = {
  high: 'success',
  medium: 'warning',
  low: 'error'
};

const Confidence: React.FC<{ confidence: GovernedAnalysisConfidence }> = ({ confidence }) => {
  const tone = responseTone(CONFIDENCE_TONE[confidence.level]);
  return (
    <ConfidenceLine data-testid="orbit-response-confidence">
      <Pill $ink={tone.ink} $fill={tone.fill}>
        <span aria-hidden="true">
          <SafetyCertificateOutlined />
        </span>
        <span>{confidence.label}</span>
      </Pill>
      {confidence.reason ? (
        <Reason>
          {confidence.reason_label ? <strong>{confidence.reason_label} </strong> : null}
          {confidence.reason}
        </Reason>
      ) : null}
    </ConfidenceLine>
  );
};

/** Figma 6424:11520 turn 1, 6410:9002 and 6473:11977: the governed analysis panel (6424:11849). */
const GovernedAnalysis: React.FC<{ block: GovernedAnalysisBlock }> = ({ block }) => {
  const emit = useResponseAction(block);
  const { chart, confidence } = block;
  // A highlight naming a segment takes that segment's colour (6424:11856 "Premier").
  const segmentInks = new Map(
    (chart?.segments ?? []).map((segment, index) => [segment.label, segmentInk(segment, index)])
  );
  return (
    <ResponseReply data-testid="orbit-response-governed_analysis" data-block-id={block.id}>
      <Panel>
        <Head>
          <Eyebrow>
            <ResponseIcon icon={block.icon} /> {block.eyebrow}
          </Eyebrow>
        </Head>
        <Body>
          <Prose>
            <HighlightedText
              text={block.text}
              highlights={block.highlights}
              inkFor={run => segmentInks.get(run)}
            />
          </Prose>
          {chart ? <GovernedDonut chart={chart} onAction={emit} /> : null}
          {confidence || block.actions.length > 0 ? (
            <div>
              {confidence ? <Confidence confidence={confidence} /> : null}
              <ResponseActions actions={block.actions} onAction={emit} align="end" />
            </div>
          ) : null}
        </Body>
      </Panel>
    </ResponseReply>
  );
};

export default GovernedAnalysis;
